import { useState, type KeyboardEvent } from 'react';
import { useDashboardStore } from '../useDashboardStore';
import { cn } from '@/lib/cn';
import {
  PlusIcon,
  TrashIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

export default function TodoList() {
  const todos = useDashboardStore((s) => s.todos);
  const addTodo = useDashboardStore((s) => s.addTodo);
  const toggleTodo = useDashboardStore((s) => s.toggleTodo);
  const deleteTodo = useDashboardStore((s) => s.deleteTodo);
  const [input, setInput] = useState('');

  const handleAdd = () => {
    const text = input.trim();
    if (!text) return;
    addTodo(text);
    setInput('');
  };

  const handleKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleAdd();
  };

  return (
    <div className="space-y-3">
      {/* Input */}
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
          placeholder="Add a task…"
          className={cn(
            'flex-1 h-8 rounded-lg border border-gray-200 bg-gray-50 px-3',
            'text-sm text-gray-900 placeholder-gray-400',
            'transition-all duration-200 ease-in-out',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
          )}
          aria-label="New task"
        />
        <button
          onClick={handleAdd}
          disabled={!input.trim()}
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-lg',
            'bg-indigo-600 text-white hover:bg-indigo-500',
            'transition-all duration-200 ease-in-out',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
            'disabled:opacity-40 disabled:cursor-not-allowed',
          )}
          aria-label="Add task"
        >
          <PlusIcon className="h-4 w-4" />
        </button>
      </div>

      {/* List */}
      <ul className="space-y-1 max-h-52 overflow-y-auto pr-1" role="list">
        {todos.map((todo) => (
          <li
            key={todo.id}
            className={cn(
              'group flex items-center gap-2.5 rounded-lg px-2.5 py-2',
              'transition-all duration-200 ease-in-out hover:bg-gray-50',
            )}
          >
            {/* Checkbox */}
            <button
              onClick={() => toggleTodo(todo.id)}
              className={cn(
                'flex h-5 w-5 shrink-0 items-center justify-center rounded border',
                'transition-all duration-200 ease-in-out',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                todo.done
                  ? 'border-indigo-600 bg-indigo-600 text-white'
                  : 'border-gray-300 text-transparent hover:border-gray-400',
              )}
              aria-label={todo.done ? `Mark "${todo.text}" incomplete` : `Mark "${todo.text}" complete`}
            >
              {todo.done && <CheckIcon className="h-3 w-3" />}
            </button>

            <span
              className={cn(
                'flex-1 text-sm truncate',
                todo.done ? 'text-gray-400 line-through' : 'text-gray-700',
              )}
            >
              {todo.text}
            </span>

            {/* Delete */}
            <button
              onClick={() => deleteTodo(todo.id)}
              className={cn(
                'flex h-6 w-6 items-center justify-center rounded opacity-0 group-hover:opacity-100',
                'text-gray-400 hover:text-red-600 hover:bg-red-50',
                'transition-all duration-200 ease-in-out',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:opacity-100',
              )}
              aria-label={`Delete "${todo.text}"`}
            >
              <TrashIcon className="h-3.5 w-3.5" />
            </button>
          </li>
        ))}
        {todos.length === 0 && (
          <li className="py-6 text-center text-sm text-gray-400">
            No tasks yet
          </li>
        )}
      </ul>
    </div>
  );
}
